import { SpiritMap } from "../../helpers/getItemFillingRangesNew";
import { BracketsItem, ItemI, PartInterface, partsProperties } from "../../../shared";
import { generateID } from "../../helpers/generateID";
import { Color, StoreWallInterface } from "../../types/store";
import { useStoreSystem } from "../../useStoreSystem";
import { getMaxHeight } from "./calcItemPosition";
import { updateRodBitrixIds } from "../../helpers/updateRodBitrixIds";
import { updateItemNameWithNomenclature } from "../../helpers/getItemNomenclatureName";
import { isBracket, isAmplifier, isEdge, isNotServicePart, isRod } from "../../../utils/typeChecker";
import { isNewSite } from "../../../utils";
import { useShelvingStore } from "../../useShelvingStore";

export const addSpiritItems = (spiritsMap: SpiritMap[], item: ItemI, currentShelving: StoreWallInterface) => {
    const sections = structuredClone(currentShelving.sections);
    const parts = useStoreSystem.getState().parts;

    const isNew: boolean = isNewSite();
    const colorItems: Color = useShelvingStore.getState().colorsItems?.brackets;

    const pickNomenkl = (src?: ItemI, clr?: Color) => {
        const byColor = src?.newNomenkl?.find((n) => n.color === clr);
        const fallback0 = src?.newNomenkl?.[0];
        if (byColor?.bitrixId)
            return { id: byColor.bitrixId, price: byColor.priceNomenkl ?? (Number(src?.PRICE) || 0) };
        if (fallback0?.bitrixId)
            return { id: fallback0.bitrixId, price: fallback0.priceNomenkl ?? (Number(src?.PRICE) || 0) };
        return { id: src?.BITRIX_OFFER_ID || "", price: Number(src?.PRICE) || 0 };
    };

    sections.forEach((section, sectionIndex) => {
        spiritsMap.forEach((spirit) => {
            if (section.id !== spirit.id) return;

            spirit.points.forEach((y) => {
                const x = (section.offset - 2) / 1000 + +item?.PROPERTIES.LENGTH / 2000;

                let z = +item.PROPERTIES.DEPTH / 2000 + 0.01;
                if (item.TYPE?.includes("Штанга")) {
                    const shelfAtHeight = section.items.find(
                        (sItem) =>
                            sItem.type?.includes("Полка") && Math.abs(sItem.position[1] - y) < 0.01 && !sItem.isSpirit
                    );
                    if (shelfAtHeight) {
                        if (shelfAtHeight.depth === 520) z = shelfAtHeight.depth / 2000;
                        else z = shelfAtHeight.depth / 1000 + 0.02;
                    }
                }

                const railHoles =
                    item?.PROPERTIES?.RAIL_HOLES &&
                    item?.PROPERTIES?.RAIL_HOLES.map((hole) => ({
                        leftRightRail: hole.leftRightRail === "Y",
                        leftLeftRail: hole.leftLeftRail === "Y",
                        rightLeftRail: hole.rightLeftRail === "Y",
                        rightRightRail: hole.rightRightRail === "Y",
                        depthFrom: +hole.depthFrom,
                        depthTo: +hole.depthTo,
                        sort: +hole.sort,
                    }));

                const positionLeftBracket = [section.offset / 1000, y, 0];
                const positionRightBracket = [
                    (section.offset - partsProperties.railing.vertical.doubleWidth) / 1000 + section.width / 1000,
                    y,
                    0,
                ];

                const currentBrackets =
                    parts?.filter(
                        (part) =>
                            item.PARTS &&
                            item.PARTS.find((itemPart) => +part.ID === +itemPart && part.TYPE && isBracket(part.TYPE))
                    ) ?? [];

                const elements = currentBrackets.map((bracket) => {
                    const holes = bracket?.PROPERTIES?.RAIL_HOLES;
                    const chosen = isNew
                        ? pickNomenkl(bracket, colorItems)
                        : { id: bracket?.BITRIX_OFFER_ID || "", price: Number(bracket?.PRICE) || 0 };
                    return {
                        bitrixId: chosen.id,
                        bitrixIdOriginal: bracket?.BITRIX_OFFER_ID || "",
                        model: bracket?.PROPERTIES.FILE_3D || "",
                        modelId: bracket?.ID || "",
                        name: bracket?.NAME,
                        type: bracket?.TYPE,
                        price: chosen.price,
                        railHoles:
                            holes &&
                            holes.map((hole) => ({
                                leftRightRail: hole.leftRightRail === "Y",
                                leftLeftRail: hole.leftLeftRail === "Y",
                                rightLeftRail: hole.rightLeftRail === "Y",
                                rightRightRail: hole.rightRightRail === "Y",
                                depthFrom: +hole.depthFrom,
                                depthTo: +hole.depthTo,
                                sort: +hole.sort,
                            })),
                    };
                });

                const newBrackets: BracketsItem = {
                    left: { elements, isVisible: true, position: positionLeftBracket },
                    right: { elements, isVisible: true, position: positionRightBracket },
                    isExist: !!currentBrackets[0]?.PROPERTIES.FILE_3D,
                };

                const positionLeftAmp = [section.offset / 1000, y, 0];
                const positionRightAmp = [
                    (section.offset - partsProperties.railing.vertical.doubleWidth) / 1000 + section.width / 1000,
                    y,
                    0,
                ];

                const currentAmps =
                    parts?.filter(
                        (part) =>
                            item.PARTS &&
                            item.PARTS.find((itemPart) => +part.ID === +itemPart && part.TYPE && isAmplifier(part.TYPE))
                    ) ?? [];

                const elementsAmps = currentAmps.map((amp) => {
                    const holes = amp?.PROPERTIES?.RAIL_HOLES;
                    const chosen = isNew
                        ? pickNomenkl(amp, colorItems)
                        : { id: amp?.BITRIX_OFFER_ID || "", price: Number(amp?.PRICE) || 0 };
                    return {
                        bitrixId: chosen.id,
                        bitrixIdOriginal: amp?.BITRIX_OFFER_ID || "",
                        model: amp?.PROPERTIES.FILE_3D || "",
                        modelId: amp?.ID || "",
                        name: amp?.NAME,
                        type: amp?.TYPE,
                        price: chosen.price,
                        picture: amp?.PICTURE,
                        railHoles:
                            holes &&
                            holes.map((hole) => ({
                                leftRightRail: hole.leftRightRail === "Y",
                                leftLeftRail: hole.leftLeftRail === "Y",
                                rightLeftRail: hole.rightLeftRail === "Y",
                                rightRightRail: hole.rightRightRail === "Y",
                                depthFrom: +hole.depthFrom,
                                depthTo: +hole.depthTo,
                                sort: +hole.sort,
                            })),
                    };
                });

                const newAmps: BracketsItem = {
                    left: { elements: elementsAmps, isVisible: true, position: positionLeftAmp },
                    right: { elements: elementsAmps, isVisible: true, position: positionRightAmp },
                    isExist: !!currentAmps[0]?.PROPERTIES.FILE_3D,
                };

                const currentRods =
                    parts?.filter(
                        (part) =>
                            item.PARTS &&
                            item.PARTS.find((itemPart) => +part.ID === +itemPart && part.TYPE && isRod(part.TYPE))
                    ) ?? [];

                const newRods: PartInterface[] = currentRods.map((rod) => {
                    const holes = rod?.PROPERTIES?.RAIL_HOLES;

                    let rodZ = +item.PROPERTIES.DEPTH / 2000 || 0.5;
                    if (item.PROPERTIES.DEPTH === "520") rodZ = 520 / 2000;

                    const chosen = isNew
                        ? pickNomenkl(rod, colorItems)
                        : { id: rod?.BITRIX_OFFER_ID || "", price: Number(rod?.PRICE) || 0 };

                    return {
                        isVisible: !!rod?.PROPERTIES.FILE_3D,
                        bitrixId: chosen.id,
                        bitrixIdOriginal: rod?.BITRIX_OFFER_ID || "",
                        bitrixIdAlt: rod?.BITRIX_OFFER_ID_ALT,
                        model: rod?.PROPERTIES.FILE_3D || "",
                        modelId: rod?.ID || "",
                        name: rod?.NAME,
                        type: rod?.TYPE,
                        price: chosen.price,
                        picture: rod?.PICTURE,
                        railHoles:
                            holes &&
                            holes.map((hole) => ({
                                leftRightRail: hole.leftRightRail === "Y",
                                leftLeftRail: hole.leftLeftRail === "Y",
                                rightLeftRail: hole.rightLeftRail === "Y",
                                rightRightRail: hole.rightRightRail === "Y",
                                depthFrom: +hole.depthFrom,
                                depthTo: +hole.depthTo,
                                sort: +hole.sort,
                            })),
                        position: [section.offset / 1000 + section.width / 2000, y, rodZ],
                    };
                });

                const currentEdges =
                    parts?.filter(
                        (part) =>
                            item.PARTS &&
                            item.PARTS.find((itemPart) => +part.ID === +itemPart && part.TYPE && isEdge(part.TYPE))
                    ) ?? [];

                const newEdges: PartInterface[] = currentEdges.map((edge) => {
                    const holes = edge?.PROPERTIES?.RAIL_HOLES;
                    const chosen = isNew
                        ? pickNomenkl(edge, colorItems)
                        : { id: edge?.BITRIX_OFFER_ID || "", price: Number(edge?.PRICE) || 0 };
                    return {
                        isVisible: !!edge?.PROPERTIES.FILE_3D,
                        bitrixId: chosen.id,
                        bitrixIdOriginal: edge?.BITRIX_OFFER_ID || "",
                        model: edge?.PROPERTIES.FILE_3D || "",
                        modelId: edge?.ID || "",
                        name: edge?.NAME,
                        type: edge?.TYPE,
                        price: chosen.price,
                        picture: edge?.PICTURE,
                        railHoles:
                            holes &&
                            holes.map((hole) => ({
                                leftRightRail: hole.leftRightRail === "Y",
                                leftLeftRail: hole.leftLeftRail === "Y",
                                rightLeftRail: hole.rightLeftRail === "Y",
                                rightRightRail: hole.rightRightRail === "Y",
                                depthFrom: +hole.depthFrom,
                                depthTo: +hole.depthTo,
                                sort: +hole.sort,
                            })),
                        position: [
                            section.offset / 1000 + +item.PROPERTIES.LENGTH / 2000,
                            y,
                            +item.PROPERTIES.DEPTH / 2000 || 0.25,
                        ],
                    };
                });

                const currentParts =
                    parts?.filter(
                        (part) =>
                            item.PARTS &&
                            item.PARTS.find(
                                (itemPart) => +part.ID === +itemPart && part.TYPE && isNotServicePart(part.TYPE)
                            )
                    ) ?? [];

                const additionalParts: any[] = [];
                if (
                    currentParts.find((part) => {
                        if (part.NAME.toLowerCase().includes("колесо")) {
                            additionalParts.push(part);
                            return true;
                        }
                    })
                ) {
                    currentParts.push(...additionalParts);
                }

                const newParts: PartInterface[] = currentParts.map((part) => {
                    const holes = part?.PROPERTIES?.RAIL_HOLES;
                    const chosen = isNew
                        ? pickNomenkl(part, colorItems)
                        : { id: part?.BITRIX_OFFER_ID || "", price: Number(part?.PRICE) || 0 };
                    return {
                        isVisible: !!part?.PROPERTIES.FILE_3D,
                        bitrixId: chosen.id,
                        bitrixIdOriginal: part?.BITRIX_OFFER_ID || "",
                        bitrixIdAlt: part?.BITRIX_OFFER_ID_ALT,
                        model: part?.PROPERTIES.FILE_3D || "",
                        modelId: part?.ID || "",
                        name: part?.NAME,
                        type: part?.TYPE,
                        price: chosen.price,
                        railHoles:
                            holes &&
                            holes.map((hole) => ({
                                leftRightRail: hole.leftRightRail === "Y",
                                leftLeftRail: hole.leftLeftRail === "Y",
                                rightLeftRail: hole.rightLeftRail === "Y",
                                rightRightRail: hole.rightRightRail === "Y",
                                depthFrom: +hole.depthFrom,
                                depthTo: +hole.depthTo,
                                sort: +hole.sort,
                            })),
                        position: [
                            section.offset / 1000 + +item.PROPERTIES.LENGTH / 2000,
                            y,
                            +item.PROPERTIES.DEPTH / 2000 || 0.25,
                        ],
                    };
                });

                const chosenMain = isNew
                    ? pickNomenkl(item, colorItems)
                    : { id: item.BITRIX_OFFER_ID, price: Number(item?.PRICE) || 0 };

                let addedItem = {
                    id: generateID(),
                    width: +item.PROPERTIES.LENGTH,
                    height: +item.PROPERTIES.HEIGHT,
                    depth: +item.PROPERTIES.DEPTH,
                    railHoles: railHoles,
                    model: item.PROPERTIES.FILE_3D,
                    modelId: item.ID,
                    bitrixId: chosenMain.id,
                    bitrixIdOriginal: item.BITRIX_OFFER_ID as string,
                    bitrixIdAlt: item.BITRIX_OFFER_ID_ALT,
                    price: chosenMain.price,
                    sectionID: section.id,
                    type: item?.TYPE || [],
                    brackets: newBrackets,
                    amps: newAmps,
                    rods: newRods,
                    edges: newEdges,
                    parts: newParts,
                    name: item.NAME,
                    isSpirit: true,
                    position: [x, y, z],
                };

                if (item.BITRIX_OFFER_ID_ALT) {
                    const updatedItem = updateItemNameWithNomenclature(addedItem, item);
                    addedItem.name = updatedItem.name;
                }

                const maxY = getMaxHeight(addedItem);
                if (maxY < y) return;

                sections[sectionIndex].items.push(addedItem);
            });
        });
    });

    if (item.TYPE?.some((t) => t === "Штанга")) {
        return updateRodBitrixIds(sections);
    }

    return sections;
};
